/* 

Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.

*/

import { useAuthContext } from '../../contexts/AuthContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useCallback, useMemo } from 'react';

type UseFilteredResultCodesList = {
  filteredResultCodes: Record<string, string | number>[];
  loadingFilteredResultCodes: boolean;
  reloadFilteredResultCodes: () => void;
};

const useFilteredResultCodesList = (actionCode: string): UseFilteredResultCodesList => {
  const { token } = useAuthContext();
  const queryClient = useQueryClient();
  const queryKey = useMemo(() => ['useFilteredResultCodesList', actionCode, token], [actionCode, token]);

  // Fetch communication data using react-query
  const { data, isLoading } = useQuery({
    queryKey: queryKey,
    queryFn: async () => {
      try {
        if (!actionCode || !token) return { data: [] };
        const request = {
          url: process.env[`REACT_APP_REST_BASE`] + `live/comment/action-code/${actionCode}/result-codes`,
          method: 'GET',
          headers: {
            'Session-Token': `${token}`,
          },
        };
        const response = await axios(request);
        console.log(`useFilteredResultCodesList.${actionCode}.response: `, response);
        return response;
      } catch (error) {
        console.error(`useFilteredResultCodesList.${actionCode}.caught: ${error.message}`);
        // Simulated fetch call. Replace this with actual API call.
        return {
          data: [],
        };
      }
    },
    refetchOnWindowFocus: false,
  });

  const reloadFilteredResultCodes = useCallback(
    () => queryClient.invalidateQueries({ queryKey }),
    [queryClient, queryKey],
  );

  return {
    filteredResultCodes: !isLoading && Array.isArray(data.data) ? data.data : [],
    loadingFilteredResultCodes: isLoading,
    reloadFilteredResultCodes,
  };
};

export default useFilteredResultCodesList;
